import { BaseAction } from "../../../core/actions/BaseAction";
import { _eventBus } from "../../../core/events/EventBus";
import { calculatePrice, convertISO8601ToDuration, convertTimeToArabic } from "../../../Lib/Utils/validationUtil";
import { bookItAgainTree } from "../../../Orchestrator/ConstantTree/BookItAgain/BookItAgainTree";
import { IBookedOffer, IBookedTrips, IBookingTypeTrip } from "../../../Orchestrator/Types/Index";
import { GetTripDetailsFromBookingActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookItAgainActionParams";
import { IGetAllTripWithoutExchangeActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/FilterTripAction";
import { TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";
import { preferTrainTrips } from "../../../Lib/Utils/booking";

export class GetTripDetailsFromBookingAction extends BaseAction<string, GetTripDetailsFromBookingActionParams> {

    override async run(): Promise<string> {
        let actionResponse = "fail"
        const { booking } = this.params!
        try {
            if (booking) {
                await getAllOfferWithoutExchangeAction.setParams(booking.bookedOffers)
                await getAllOfferWithoutExchangeAction.execute()
                let allBookedOfferWithoutExchange: IBookedOffer[] = getAllOfferWithoutExchangeAction.result!

                const params: IGetAllTripWithoutExchangeActionParams = {
                    bookedOffers: allBookedOfferWithoutExchange,
                    trips: booking.trips
                }
                await getAllTripWithoutExchangeAction.setParams(params)
                await getAllTripWithoutExchangeAction.execute()
                const allTripWithoutExchange: IBookingTypeTrip[] = getAllTripWithoutExchangeAction.result!

                // Prefer trips with a TRAIN leg — a standalone onward-shuttle trip
                // can share the same direction as the train trip and must not
                // hijack this lookup (see preferTrainTrips doc).
                const outboundTrip = preferTrainTrips(allTripWithoutExchange).find(trip => trip.direction === TripTypeEnum.OUT_BOUND)
                const inboundTrip = preferTrainTrips(allTripWithoutExchange).find(trip => trip.direction === TripTypeEnum.IN_BOUND)

                const outboundTripId = outboundTrip?.id
                const inboundTripId = inboundTrip?.id

                const outboundOffers = outboundTripId
                    ? allBookedOfferWithoutExchange.filter(
                        d => d.tripCoverage?.coveredTripId === outboundTripId
                    )
                    : []
                
                const inboundOffers = inboundTripId 
                    ? allBookedOfferWithoutExchange.filter(
                        d => d.tripCoverage?.coveredTripId === inboundTripId
                    )
                    : []

                const outboundSummary = outboundOffers?.find(d => d?.tripCoverage?.coveredTripId === outboundTripId)?.products[0]?.summary || ""
                const inboundSummary = inboundOffers?.find(d => d?.tripCoverage?.coveredTripId === inboundTripId)?.products[0]?.summary || ""

                // Get offer IDs as array of strings
                const outboundOfferIds = outboundOffers
                    .map(offer => offer.offerId)
                    .filter((id): id is string => !!id)
                const outBoundOfferID: string | string[] | undefined = outboundOfferIds.length > 0 
                    ? (outboundOfferIds.length === 1 ? outboundOfferIds[0] : outboundOfferIds)
                    : undefined

                const inboundOfferIds = inboundOffers
                    .map(offer => offer.offerId)
                    .filter((id): id is string => !!id)
                const inboundOfferID: string | string[] | undefined = inboundOfferIds.length > 0 
                    ? (inboundOfferIds.length === 1 ? inboundOfferIds[0] : inboundOfferIds)
                    : undefined

                const bookedTrip: IBookedTrips = {
                    bookingId: booking.id,
                    fromName: outboundTrip?.legs[0]?.timedLeg?.start?.stopPlaceName || "",
                    toName: outboundTrip?.legs[outboundTrip.legs.length - 1]?.timedLeg?.end?.stopPlaceName || "",
                    from: outboundTrip?.origin?.stopPlaceRef || "",
                    to: outboundTrip?.destination?.stopPlaceRef || "",
                    departureTime: outboundTrip ? convertTimeToArabic(outboundTrip.startTime) : "",
                    arrivalTime: outboundTrip ? convertTimeToArabic(outboundTrip.endTime) : "",
                    class: "",
                    summary: outboundSummary,
                    type: booking.passengers[0]?.type || 'Unknown',
                    passengers: booking.passengers.map((passenger) => ({
                        ...passenger,
                        detail: {
                            ...passenger?.detail || {},
                            firstName: passenger?.detail?.firstName || '',
                            lastName: passenger?.detail?.lastName || '',
                            email: passenger?.detail?.email || '',
                        },
                    })),
                    duration: outboundTrip ? convertISO8601ToDuration(outboundTrip.duration) : "",
                    trainNumber: booking.bookingCode,
                    stops: outboundTrip && outboundTrip.transfers > 0 ? Array(outboundTrip.transfers).fill("Stop") : [],
                    status: "FULFILLED",
                    startDate: outboundTrip?.startTime ? new Date(outboundTrip.startTime).toLocaleDateString("en-US", {
                        weekday: "short", // "Tue"
                        day: "2-digit", // "02"
                        month: "short", // "Apr"
                    }) : "",
                    ticketLink: booking?.fulfillments?.length > 0 && booking?.fulfillments[0]?.fulfillmentDocuments[0]?.downloadLink || "",
                    arrivalDateTime: outboundTrip?.endTime || "",
                    departureDateTime: outboundTrip?.startTime || "",
                    bookingType: inboundTrip ? "roundTrip" : "oneWayTrip",
                    outBoundOfferID: outBoundOfferID,
                    confirmedPrice: calculatePrice(booking.confirmedPrice),
                    inboundOfferID: inboundOfferID,
                    inboundData: inboundTrip && inboundOffers.length > 0 ? {
                        departureTime: convertTimeToArabic(inboundTrip.startTime),
                        arrivalTime: convertTimeToArabic(inboundTrip.endTime),
                        arrivalDateTime: inboundTrip.endTime,
                        departureDateTime: inboundTrip.startTime,
                        summary: inboundSummary,
                        duration: inboundTrip.duration ? convertISO8601ToDuration(inboundTrip.duration) : "",
                        stops: inboundTrip.transfers > 0 ? Array(inboundTrip.transfers).fill("Stop") : []
                    } : undefined,
                };                

                setTimeout(() => {
                    _eventBus.emit({
                        eventName: bookItAgainTree.Modal.Show,
                        data: bookedTrip,
                    });
                }, 100);
            }
        }
        catch (error) {
            console.error("Error during booking again:", error);
        } finally {
            return actionResponse
        }
    }
}

export const getTripDetailsFromBookingAction = new GetTripDetailsFromBookingAction(); 